import React, { useEffect, useRef, useState } from "react";
import { AgentStep } from "../../../types/chatWidget";

// ─── Keyframes (injected once) ────────────────────────────────────────────────

const KEYFRAMES = `
@keyframes cl-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes cl-step-in {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes cl-pulse-ring {
  0%   { box-shadow: 0 0 0 0 rgba(37,99,235,0.36); }
  70%  { box-shadow: 0 0 0 8px rgba(37,99,235,0); }
  100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); }
}
@keyframes cl-blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
}
`;

let keyframesInjected = false;
function ensureKeyframes() {
  if (keyframesInjected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
  keyframesInjected = true;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function elapsedSince(iso: string): number {
  return Math.max(0, Date.now() - new Date(iso).getTime());
}

function fmt(ms: number): string {
  if (ms <= 0) return "";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${(s % 60).toFixed(0)}s`;
}

const HUMAN_LABELS: Record<string, string> = {
  "Chat Input": "Receiving message",
  "Agent": "Processing with AI",
  "Chat Output": "Formulating response",
  "Prompt": "Preparing instructions",
  "URL": "Fetching web content",
  "OpenAI": "Querying model",
  "Parse Data": "Extracting information",
  "Search": "Searching knowledge base",
  "Memory": "Retrieving memory",
  "Tool": "Executing tool",
  "Retriever": "Retrieving documents",
  "Router": "Routing query",
  "Conditional": "Evaluating conditions",
};

function humanLabel(title: string): string {
  return HUMAN_LABELS[title] || title;
}


// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg
      width="13" height="13" viewBox="0 0 13 13" fill="none"
      style={{ animation: "cl-spin 0.85s linear infinite", flexShrink: 0, display: "block" }}
      aria-hidden
    >
      <circle cx="6.5" cy="6.5" r="5" stroke="#dbeafe" strokeWidth="1.8" />
      <path d="M11.5 6.5A5 5 0 0 0 6.5 1.5" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

// ─── Status Dot ───────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: AgentStep["status"] }) {
  if (status === "running") {
    return (
      <span style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: "#eff6ff",
        flexShrink: 0,
      }}>
        <Spinner />
      </span>
    );
  }

  if (status === "completed") {
    return (
      <span style={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        backgroundColor: "#10b981",
        border: "1.5px solid #10b981",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}>
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden>
          <path d="M1.5 4.5l2.2 2.2L7.5 2.5" stroke="#ffffff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }

  if (status === "failed") {
    return (
      <span style={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        backgroundColor: "#fee2e2",
        border: "1.5px solid #fca5a5",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}>
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden>
          <path d="M2.5 2.5l4 4M6.5 2.5l-4 4" stroke="#dc2626" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
    );
  }

  // pending
  return (
    <span style={{
      width: 18,
      height: 18,
      borderRadius: "50%",
      border: "1.5px solid #d1d5db",
      backgroundColor: "transparent",
      display: "inline-flex",
      flexShrink: 0,
    }} />
  );
}

// ─── LiveTimer ────────────────────────────────────────────────────────────────

function LiveTimer({ step }: { step: AgentStep }) {
  const isRunning = step.status === "running";
  const [ms, setMs] = useState(() =>
    isRunning ? elapsedSince(step.started_at) : (step.duration_ms ?? 0),
  );
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isRunning) {
      setMs(step.duration_ms ?? 0);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }
    const tick = () => {
      setMs(elapsedSince(step.started_at));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [isRunning, step.started_at, step.duration_ms]);

  const label = fmt(ms);
  if (!label) return null;
  return (
    <span style={{
      fontSize: 11,
      color: isRunning ? "#2563eb" : "#94a3b8",
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      fontWeight: isRunning ? 500 : 400,
    }}>
      {label}
    </span>
  );
}

// ─── Tool Badges ──────────────────────────────────────────────────────────────

function ToolBadges({ tools }: { tools: string[] }) {
  if (!tools.length) return null;
  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", marginTop: 3 }}>
      {tools.map((tool) => (
        <span key={tool} style={{
          fontSize: 10,
          fontWeight: 500,
          color: "#475569",
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 999,
          padding: "2px 7px",
          whiteSpace: "nowrap",
        }}>
          {tool}
        </span>
      ))}
    </span>
  );
}

// ─── StepRow ──────────────────────────────────────────────────────────────────

const useAnimateIn = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return ready;
};

function StepRow({ step }: { step: AgentStep }) {
  const animated = useAnimateIn();
  const isRunning = step.status === "running";
  const isPending = step.status === "pending";
  const isFailed = step.status === "failed";

  const labelColor = isFailed ? "#dc2626" : isPending ? "#9ca3af" : isRunning ? "#111827" : "#374151";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        padding: "8px 10px",
        borderRadius: 10,
        backgroundColor: isRunning ? "#eff6ff" : isFailed ? "#fef2f2" : "#ffffff",
        border: `1px solid ${isRunning ? "#bfdbfe" : isFailed ? "#fecaca" : "#eef2f7"}`,
        opacity: animated ? 1 : 0,
        transform: animated ? "translateY(0)" : "translateY(5px)",
        transition: "opacity 0.25s ease, transform 0.25s ease, background-color 0.2s ease, border-color 0.2s ease",
      }}
    >
      {/* Main row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <StatusDot status={step.status} />
        <span style={{
          flex: 1,
          fontSize: 13,
          fontWeight: isRunning ? 600 : 500,
          color: labelColor,
          lineHeight: 1.4,
        }}>
          {humanLabel(step.title)}
        </span>
        {!isPending && <LiveTimer step={step} />}
      </div>

      {/* Tool badges */}
      {step.tools && step.tools.length > 0 && (
        <div style={{ paddingLeft: 26 }}>
          <ToolBadges tools={step.tools} />
        </div>
      )}

      {/* Live reasoning snippet — provided by backend */}
      {step.reasoning && (
        <p style={{
          margin: 0,
          fontSize: 12,
          color: "#64748b",
          fontStyle: "italic",
          paddingLeft: 26,
          lineHeight: 1.45,
          animation: isRunning ? "cl-blink 2.5s ease-in-out infinite" : "none",
        }}>
          {step.reasoning}
        </p>
      )}

      {/* Detail or error */}
      {(step.detail || step.error) && !step.reasoning && (
        <p style={{
          margin: 0,
          fontSize: 12,
          color: isFailed ? "#dc2626" : "#64748b",
          paddingLeft: 26,
          lineHeight: 1.45,
        }}>
          {step.error ?? step.detail}
        </p>
      )}
    </div>
  );
}

// ─── TimelineConnector ────────────────────────────────────────────────────────

function TimelineConnector({ completed }: { completed: boolean }) {
  return (
    <div style={{
      width: 1.5,
      height: 10,
      marginLeft: 8,
      backgroundColor: completed ? "#10b981" : "#dbeafe",
      transition: "background-color 0.4s ease",
      flexShrink: 0,
    }} />
  );
}

// ─── PhaseGroup ───────────────────────────────────────────────────────────────

function PhaseGroup({
  phase,
  children,
  isLast,
}: {
  phase: AgentStep;
  children: AgentStep[];
  isLast: boolean;
}) {
  const [open, setOpen] = useState(phase.status === "running" || isLast);
  const isRunning = phase.status === "running";
  const isCompleted = phase.status === "completed";
  const isFailed = phase.status === "failed";

  useEffect(() => {
    if (isRunning) setOpen(true);
    else if (isCompleted && !isLast) setOpen(false);
  }, [isRunning, isCompleted, isLast]);

  const chevronColor = isFailed ? "#dc2626" : isRunning ? "#2563eb" : "#94a3b8";
  const headerColor = isFailed ? "#dc2626" : isRunning ? "#0f172a" : "#334155";

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Phase header button */}
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          background: isRunning ? "#eff6ff" : "#ffffff",
          border: `1px solid ${isRunning ? "#bfdbfe" : "#eef2f7"}`,
          borderRadius: 10,
          padding: "8px 10px",
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
        }}
      >
        {/* Phase status dot */}
        <StatusDot status={phase.status} />

        {/* Phase icon + label */}
        <span style={{ fontSize: 13, fontWeight: 500, color: headerColor, flex: 1, lineHeight: 1.4 }}>
          {humanLabel(phase.title)}
        </span>

        <LiveTimer step={phase} />

        {/* Chevron */}
        <svg
          width="12" height="12" viewBox="0 0 12 12" fill="none"
          style={{
            transition: "transform 0.2s ease",
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            flexShrink: 0,
          }}
          aria-hidden
        >
          <path d="M2 4l4 4 4-4" stroke={chevronColor} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Children (vertices) */}
      <div
        style={{
          overflow: "hidden",
          maxHeight: open ? `${children.length * 80 + 20}px` : 0,
          transition: "max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {children.length > 0 && (
          <div style={{
            borderLeft: `2px solid ${isCompleted ? "#10b981" : isRunning ? "#bfdbfe" : "#e2e8f0"}`,
            marginLeft: 8,
            paddingLeft: 14,
            paddingTop: 8,
            paddingBottom: 4,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            transition: "border-color 0.4s ease",
          }}>
            {children.map((child, i) => (
              <React.Fragment key={child.step_id}>
                {i > 0 && (
                  <TimelineConnector completed={child.status === "completed"} />
                )}
                <StepRow step={child} />
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AgentProgressSteps ───────────────────────────────────────────────────────

interface AgentProgressStepsProps {
  steps: AgentStep[];
  phaseTitle?: string; // optional override for the card header
}

export default function AgentProgressSteps({ steps, phaseTitle }: AgentProgressStepsProps) {
  // inject CSS keyframes on first render
  useEffect(() => { ensureKeyframes(); }, []);

  if (steps.length === 0) return null;

  const phases = steps.filter((s) => s.kind === "phase");
  const vertices = steps.filter((s) => s.kind === "vertex");

  // Derive an overall title for the card header from the first running/pending phase
  const activePhase = phases.find((p) => p.status === "running") ?? phases[phases.length - 1];
  const cardTitle = phaseTitle ?? (activePhase ? humanLabel(activePhase.title) : "Processing");

  // Overall card status
  const anyRunning = steps.some((s) => s.status === "running");
  const anyFailed = steps.some((s) => s.status === "failed");
  const allDone = steps.every((s) => s.status === "completed" || s.status === "failed");

  const headerDotColor = anyFailed ? "#dc2626" : anyRunning ? "#3b82f6" : "#16a34a";

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 0,
      fontFamily: "inherit",
      animation: "cl-step-in 0.3s ease both",
      color: "#0f172a",
    }}>
      {/* ── Card header ──────────────────────────────────────── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "12px 13px",
        borderBottom: "1px solid #e2e8f0",
        background: "rgba(248,250,252,0.88)",
      }}>
        {/* Pulse dot */}
        <span style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: headerDotColor,
          flexShrink: 0,
          animation: anyRunning ? "cl-pulse-ring 1.6s ease-out infinite" : "none",
          display: "inline-block",
        }} />
        <span style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#0f172a",
          letterSpacing: 0,
          flex: 1,
        }}>
          {cardTitle}
        </span>
        {allDone && !anyFailed && (
          <span style={{
            fontSize: 11,
            color: "#047857",
            fontWeight: 700,
            backgroundColor: "#d1fae5",
            border: "1px solid #a7f3d0",
            borderRadius: 999,
            padding: "2px 7px",
          }}>
            Done
          </span>
        )}
        {anyFailed && (
          <span style={{
            fontSize: 11,
            color: "#b91c1c",
            fontWeight: 700,
            backgroundColor: "#fee2e2",
            border: "1px solid #fecaca",
            borderRadius: 999,
            padding: "2px 7px",
          }}>
            Failed
          </span>
        )}
      </div>

      {/* ── Step list ────────────────────────────────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "11px 11px 12px" }}>
        {phases.length === 0 ? (
          /* Flat list when no phases */
          steps.map((s, i) => (
            <React.Fragment key={s.step_id}>
              {i > 0 && <TimelineConnector completed={steps[i - 1].status === "completed"} />}
              <StepRow step={s} />
            </React.Fragment>
          ))
        ) : (
          /* Grouped by phase */
          phases.map((phase, i) => {
            const phaseTime = new Date(phase.started_at).getTime();
            const nextPhaseTime = phases[i + 1]
              ? new Date(phases[i + 1].started_at).getTime()
              : Infinity;

            const phaseChildren = vertices.filter((v) => {
              const vt = new Date(v.started_at).getTime();
              return vt >= phaseTime && vt < nextPhaseTime;
            });

            // Skip silent completed phases with no children
            if (phaseChildren.length === 0 && phase.status === "completed") return null;

            return (
              <React.Fragment key={phase.step_id}>
                {i > 0 && phases[i - 1].status !== "pending" && (
                  <TimelineConnector completed={phases[i - 1].status === "completed"} />
                )}
                <PhaseGroup
                  phase={phase}
                  children={phaseChildren}
                  isLast={i === phases.length - 1}
                />
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* ── Pending "next steps" inferred from next_step_ids ─ */}
      {(() => {
        const lastStep = steps[steps.length - 1];
        const pendingIds = lastStep?.next_step_ids ?? [];
        const alreadyKnownIds = new Set(steps.map((s) => s.step_id));
        const trulyPending = pendingIds.filter((id) => !alreadyKnownIds.has(id));
        if (!trulyPending.length) return null;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 11px 12px" }}>
            {trulyPending.map((id) => (
              <React.Fragment key={id}>
                <TimelineConnector completed={false} />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StatusDot status="pending" />
                  <span style={{ fontSize: 13, color: "#9ca3af" }}>Pending…</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        );
      })()}
    </div>
  );
}
